// src/courses/CourseCatalog.js
// 🎓 TICKET QBFKZ8 - Catálogo centralizado de cursos
// Fuente única de verdad para todos los cursos disponibles

/**
 * @typedef {Object} Activity
 * @property {string} id - ID único de la actividad
 * @property {'resource' | 'vrt_simulation' | 'epl_lab'} type - Tipo de actividad
 * @property {string} title - Título de la actividad
 * @property {number} estimatedMinutes - Minutos estimados de duración
 * @property {string} refId - 🎓 TICKET JPRXH6 - ID de referencia real (agentId VRT, labId EPL, slug resource)
 */

/**
 * @typedef {Object} Module
 * @property {string} id - ID único del módulo
 * @property {string} title - Título del módulo
 * @property {string} description - Descripción del módulo
 * @property {Activity[]} activities - Actividades del módulo
 */

/**
 * @typedef {Object} Course
 * @property {string} id - URL-safe identifier (ej: "design-thinking")
 * @property {string} title - Título del curso
 * @property {string} shortDescription - Descripción corta (1 línea)
 * @property {number} estimatedHours - Horas estimadas de duración
 * @property {string[]} skills - Habilidades que desarrolla el curso
 * @property {'active' | 'draft'} status - Estado del curso
 * @property {string} coverIcon - Emoji o identificador de ícono
 * @property {Module[]} modules - Módulos del curso con sus actividades
 */

/**
 * Catálogo completo de cursos
 * @type {Course[]}
 */
export const COURSES = [
  {
    id: 'design-thinking',
    title: 'Design Thinking',
    shortDescription: 'Aprende metodologías ágiles para innovar y resolver problemas complejos con enfoque en el usuario.',
    estimatedHours: 5,
    skills: ['Empatía', 'Framing', 'Ideación', 'Prototipado', 'Storytelling'],
    status: 'active',
    coverIcon: '🎨',
    modules: [
      {
        id: 'empathy',
        title: 'Empatía estratégica',
        description: 'Comprender problemas reales del usuario mediante entrevistas y observación',
        activities: [
          {
            id: 'empathy_intro',
            type: 'resource',
            title: 'Cómo entrevistar sin sesgo',
            estimatedMinutes: 10,
            refId: 'empathy_intro_md' // 🎓 TICKET JPRXH6
          },
          {
            id: 'empathy_sim',
            type: 'vrt_simulation',
            title: 'Simulación: Cliente Sparring',
            estimatedMinutes: 15,
            refId: 'client_sparring_v1' // 🎓 TICKET JPRXH6 - agentId VRT real
          },
          {
            id: 'empathy_lab',
            type: 'epl_lab',
            title: 'Lab: Repaso de Empatía',
            estimatedMinutes: 20,
            refId: 'empathy_assessment_lab' // 🎓 TICKET JPRXH6 - labId EPL
          }
        ]
      },
      {
        id: 'define',
        title: 'Definición del problema',
        description: 'Sintetizar insights para definir el problema correcto a resolver',
        activities: [
          {
            id: 'define_intro',
            type: 'resource',
            title: 'Del insight al problema accionable',
            estimatedMinutes: 12,
            refId: 'problem_framing_guide' // 🎓 TICKET JPRXH6
          },
          {
            id: 'define_sim',
            type: 'vrt_simulation',
            title: 'Simulación: Sparring de Framing',
            estimatedMinutes: 18,
            refId: 'framing_sparring_v1' // 🎓 TICKET JPRXH6
          },
          {
            id: 'define_lab',
            type: 'epl_lab',
            title: 'Lab: Define tu problema',
            estimatedMinutes: 25,
            refId: 'problem_definition_lab' // 🎓 TICKET JPRXH6
          }
        ]
      },
      {
        id: 'ideation',
        title: 'Ideación',
        description: 'Generar soluciones creativas mediante técnicas de divergencia y convergencia',
        activities: [
          {
            id: 'ideation_intro',
            type: 'resource',
            title: 'Técnicas de brainstorming estructurado',
            estimatedMinutes: 15,
            refId: 'brainstorming_techniques' // 🎓 TICKET JPRXH6
          },
          {
            id: 'ideation_sim',
            type: 'vrt_simulation',
            title: 'Simulación: Sesión de ideación',
            estimatedMinutes: 20,
            refId: 'ideation_facilitator_v1' // 🎓 TICKET JPRXH6
          },
          {
            id: 'ideation_lab',
            type: 'epl_lab',
            title: 'Lab: Tu árbol de ideas',
            estimatedMinutes: 30,
            refId: 'ideation_tree_lab' // 🎓 TICKET JPRXH6
          }
        ]
      },
      {
        id: 'prototype',
        title: 'Prototipado',
        description: 'Construir prototipos rápidos para validar hipótesis con usuarios reales',
        activities: [
          {
            id: 'prototype_intro',
            type: 'resource',
            title: 'Del papel al prototipo digital',
            estimatedMinutes: 10,
            refId: 'rapid_prototyping_guide' // 🎓 TICKET JPRXH6
          },
          {
            id: 'prototype_sim',
            type: 'vrt_simulation',
            title: 'Simulación: Presentación de prototipo',
            estimatedMinutes: 20,
            refId: 'pitch_prototype_v1' // 🎓 TICKET JPRXH6
          },
          {
            id: 'prototype_lab',
            type: 'epl_lab',
            title: 'Lab: Validación de prototipo',
            estimatedMinutes: 35,
            refId: 'prototype_validation_lab' // 🎓 TICKET JPRXH6
          }
        ]
      }
    ]
  },
  {
    id: 'decision-support',
    title: 'Decision Support System',
    shortDescription: 'Domina herramientas de análisis y toma de decisiones estratégicas basadas en datos.',
    estimatedHours: 4,
    skills: ['Riesgo', 'Priorización', 'Escenarios', 'Decisión ejecutiva'],
    status: 'draft', // No visible en dashboard
    coverIcon: '📊',
    modules: [
      {
        id: 'risk_analysis',
        title: 'Análisis de riesgo',
        description: 'Identificar y cuantificar riesgos en decisiones estratégicas',
        activities: [
          {
            id: 'risk_intro',
            type: 'resource',
            title: 'Introducción al análisis de riesgo',
            estimatedMinutes: 15,
            refId: 'risk_analysis_intro' // 🎓 TICKET JPRXH6
          },
          {
            id: 'risk_sim',
            type: 'vrt_simulation',
            title: 'Simulación: Matriz de riesgo',
            estimatedMinutes: 20,
            refId: 'risk_matrix_simulator_v1' // 🎓 TICKET JPRXH6
          },
          {
            id: 'risk_lab',
            type: 'epl_lab',
            title: 'Lab: Tu análisis de riesgo',
            estimatedMinutes: 25,
            refId: 'risk_analysis_lab' // 🎓 TICKET JPRXH6
          }
        ]
      },
      {
        id: 'scenario_planning',
        title: 'Planificación por escenarios',
        description: 'Construir y evaluar escenarios alternativos para tomar mejores decisiones',
        activities: [
          {
            id: 'scenario_intro',
            type: 'resource',
            title: 'Modelado de escenarios estratégicos',
            estimatedMinutes: 12,
            refId: 'scenario_planning_guide' // 🎓 TICKET JPRXH6
          },
          {
            id: 'scenario_sim',
            type: 'vrt_simulation',
            title: 'Simulación: Guerra de escenarios',
            estimatedMinutes: 18,
            refId: 'scenario_war_game_v1' // 🎓 TICKET JPRXH6
          },
          {
            id: 'scenario_lab',
            type: 'epl_lab',
            title: 'Lab: Escenarios de tu decisión',
            estimatedMinutes: 30,
            refId: 'scenario_decision_lab' // 🎓 TICKET JPRXH6
          }
        ]
      }
    ]
  }
];

/**
 * Busca un curso por su ID
 * @param {string} courseId - ID del curso a buscar
 * @returns {Course | null} El curso encontrado o null si no existe
 */
export function getCourseById(courseId) {
  const course = COURSES.find(c => c.id === courseId);
  return course || null;
}

/**
 * Obtiene solo los cursos activos (visibles)
 * @returns {Course[]} Array de cursos con status 'active'
 */
export function getActiveCourses() {
  return COURSES.filter(c => c.status === 'active');
}
